import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

function PageInfo({ 
  metaType = 'website', 
  metaUrl, 
  metaTitle, 
  metaDescription, 
  metaImage, 
  twitterCard = 'summary_large_image', 
  twitterSite = '@CrossoverGrid', 
  twitterCreator, 
  twitterTitle, 
  twitterDescription, 
  twitterImage, 
  canonicalLink, 
  title, 
  keywords,
  ogSiteName = 'Crossover Grid',
  ogLocale = 'en_US'
}) {
  const location = useLocation();
  const baseUrl = 'https://crossovergrid.com';
  
  // Always use the actual pathname for canonical URL to ensure uniqueness
  const currentPath = location.pathname + location.search;
  const canonicalPath = canonicalLink || currentPath;
  const urlPath = metaUrl || currentPath;
  
  // Ensure paths start with / and build full URLs
  const normalizedCanonical = canonicalPath.startsWith('http') 
    ? canonicalPath 
    : `${baseUrl}${canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`}`;
  
  const normalizedUrl = urlPath.startsWith('http') 
    ? urlPath 
    : `${baseUrl}${urlPath.startsWith('/') ? urlPath : `/${urlPath}`}`;
  
  const fullImage = metaImage ? (metaImage.startsWith('http') ? metaImage : `${baseUrl}${metaImage}`) : `${baseUrl}/og-image-nba.png`;
  const fullTwitterImage = twitterImage ? (twitterImage.startsWith('http') ? twitterImage : `${baseUrl}${twitterImage}`) : fullImage;
  
  const finalTitle = title || metaTitle || 'Crossover Grid: Daily Sports Trivia';
  const finalMetaTitle = metaTitle || finalTitle;
  const finalTwitterTitle = twitterTitle || finalMetaTitle;
  const finalDescription = metaDescription || 'Put your sports & movies knowledge to the test and try to fill out an immaculate grid! New grid every day!';
  const finalTwitterDescription = twitterDescription || finalDescription;

  return (
    <Helmet>
      <meta charSet="UTF-8" />
      <meta name="description" content={finalDescription} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={metaType} />
      <meta property="og:url" content={normalizedUrl} />
      <meta property="og:title" content={finalMetaTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:secure_url" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={finalMetaTitle} />
      <meta property="og:site_name" content={ogSiteName} />
      <meta property="og:locale" content={ogLocale} />
      
      {/* Keywords */}
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={Array.isArray(keywords) ? keywords.join(', ') : keywords} />
      )}
      
      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content={twitterSite} />
      {twitterCreator && <meta name="twitter:creator" content={twitterCreator} />}
      <meta name="twitter:title" content={finalTwitterTitle} />
      <meta name="twitter:description" content={finalTwitterDescription} />
      <meta name="twitter:image" content={fullTwitterImage} />
      
      {/* Canonical URL - Always unique per page */}
      <link rel="canonical" href={normalizedCanonical} />
      
      {/* Page Title */}
      <title>{finalTitle}</title>
    </Helmet>
  );
}

PageInfo.propTypes = {
  metaType: PropTypes.string,
  metaUrl: PropTypes.string,
  metaTitle: PropTypes.string,
  metaDescription: PropTypes.string,
  metaImage: PropTypes.string,
  twitterCard: PropTypes.string,
  twitterSite: PropTypes.string,
  twitterCreator: PropTypes.string,
  twitterTitle: PropTypes.string,
  twitterDescription: PropTypes.string,
  twitterImage: PropTypes.string,
  canonicalLink: PropTypes.string,
  title: PropTypes.string,
  keywords: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  ogSiteName: PropTypes.string,
  ogLocale: PropTypes.string,
};

export default PageInfo;















